import * as d3 from '~/app/custom-d3'
import numeral from 'numeral'

export default class RiskGraph {
  constructor(el, funds) {
    this.el = el
    this.margin = {
      top: 20,
      left: 50,
      right: 10,
      bottom: 20
    }

    this.funds = funds
    this.options = this.generateBounds()
    this.setup(d3.entries(funds))

    this.callbacks = {};
  }

  on(event, callback) {
    this.callbacks[event] = callback
  }

  generateBounds() {
    var bounds = this.el.getBoundingClientRect(),
        height = 300,
        width = 1100
    if(height > bounds.width) {
      height = bounds.width * 0.8
    }
    if(width > bounds.width) {
      width = bounds.width
    }
    return {
      height: height,
      width: width
    }
  }

  setup(funds) {
    this.svg = d3.select(this.el).append("svg")
        .attr('class', 'graph')
        .attr("width", this.options.width)
        .attr("height", this.options.height);

    // X-axis
    this.xAxisEl = this.svg.append("g")
                 .attr("class", "x axis")
                 .attr("transform", "translate("+this.margin.left+"," + (this.options.height-this.margin.top-10) + ")");
    this.xAxisEl.append("text")
           .attr("class", "text-end text-lg")
           .attr("y", -18)
           .attr("x", this.options.width-100)
           .attr("dy", ".71em")
           .text("Historical Risk");

    // Y-axis
    this.yAxisEl = this.svg.append("g")
                 .attr("class", "y axis")
                 .attr("transform", "translate("+this.margin.left+","+this.margin.top+")");
    this.yAxisEl.append("text")
           .attr("class", "text-end text-lg rotate--90")
           .attr("y", 6)
           .attr("x", this.margin.top*-1)
           .attr("dy", ".71em")
           .text("Historical Returns");


     // Risk/Reward path
     var g = this.svg.append('g')
         .attr('transform', "translate(" + this.margin.left + "," + this.margin.top + ")");
     this.path = g.append('path')
       .attr('class', 'line stroke-grey-700 stroke-width-base stroke-dasharray-dashed-lg');



    // Tooltips
    this.tip = d3.tip().attr('class', 'd3-tip rounded').html(function(fund) {
      return `<div class='text-${fund.style.lightClass}'><p class='text-base mb-2 leading-relaxed'>${fund.label}</p><p class='text-sm'><span class='font-semibold'>Avg gain:</span> ${numeral(fund.computed.avg).format('0.0%')}<br/><span class='font-semibold'>Risk:</span> ${numeral(fund.computed.risk).format('0.0')}</p></div>`;
    }).offset([-18, 0]);
    this.svg.call(this.tip)


    // Points groups
    this.cirlces = this.svg.append("g")
       .attr("class", "points")
       .attr("transform", "translate(" + this.margin.left + "," + this.margin.top + ")");

    this.cirlces.selectAll('circle')
        .data(funds)
        .enter()
        .append('circle')
        .attr('r', 8)
        .attr('sector', (d) => d.key)
        .style('fill', (d) => d.value.style.darkColor)
        .style('stroke', (d) => d.value.style.darkColor)
        .style('stroke-width', 4)
        .on('mouseenter', (fund, index, element) => {
          this.callbacks['activate'](fund.key)
        })
        .on('mouseleave', (d) => {
          this.callbacks['activate']()
        })
  }

  update(fundObject) {
    var funds = d3.entries(fundObject)
    this.updateYAxis(funds)
    this.updateXAxis(funds)
    this.updateCircles(funds)
    this.updateRiskRewardLine()
  }


  // X-axis is for the risk
  updateXAxis(funds) {
    var maxRisk = d3.max(funds, (f) => +f.value.computed.risk)
    if(maxRisk < 10) { maxRisk = 10; }
    this.xScale = d3.scaleLinear()
        .domain([0,maxRisk+2])
        .range([0, this.options.width - this.margin.left - this.margin.right]);

    var xTickCount = this.options.width / 100;
    if(xTickCount > 20) { xTickCount = 20; }

    var xAxis = d3.axisBottom().scale(this.xScale).ticks(xTickCount)
    this.xAxisEl.call(xAxis);
  }

  // Y-axis is the average returns
  updateYAxis(funds) {
    var maxAvg = d3.max(funds, (f) => parseFloat(f.value.computed.avg)*100)
    var minAvg = d3.min(funds, (f) => parseFloat(f.value.computed.avg)*100)
    if(minAvg > 0) { minAvg = 0; }
    this.yScale = d3.scaleLinear()
        .domain([maxAvg, minAvg])
        .range([0, this.options.height - this.margin.bottom - this.margin.top])

    var yAxis = d3.axisLeft().scale(this.yScale).ticks(6).tickFormat((d) => `${d}%`)
    this.yAxisEl.call(yAxis);
  }

  // Place all circles
  updateCircles(funds) {
    var circles = this.cirlces.selectAll('circle').data(funds)

    circles.transition()
      .delay((d,i) => i*75)
      .duration(1000)
      .attr('cy', (d) => this.yScale(parseFloat(d.value.computed.avg)*100))
      .attr('cx', (d) => this.xScale(d.value.computed.risk))

  }

  // Draw the Risk/Reward line
  updateRiskRewardLine(funds) {
    var linePath = d3.path(),
        startX = this.xScale(1),
        startY = this.yScale(1);
    linePath.moveTo(startX, startY);
    linePath.bezierCurveTo(startX, startY, this.xScale(6), this.yScale(5), this.xScale(20), this.yScale(10));
    linePath.lineTo(this.xScale(30), this.yScale(11))
    this.path.transition()
             .duration(1000)
             .attr('d', linePath.toString())
  }

  highlight(sector) {
    if(sector && sector.length > 0) {
      var circle = this.cirlces.select(`[sector=${sector}]`),
          fund = this.funds[sector]
      this.cirlces.select(`[sector=${sector}]`).transition(150).attr('r', 16)
      this.tip.style('color', fund.style.darkColor)
              .style('background-color', fund.style.darkColor)
      this.tip.show(fund, circle.node())
    } else {
      this.cirlces.selectAll('circle').transition(500).attr('r', 8)
      this.tip.hide()
    }
  }
}
